/**
 * Workspace administration (CLAUDE.md 12.2): list, quota, activate/deactivate.
 *
 * Quota is edited in GB rather than bytes. An administrator setting a per-firm limit is
 * thinking in gigabytes, and a text box holding 53687091200 is a transcription error
 * waiting to happen.
 *
 * A personal workspace cannot be deactivated. The server refuses it (deactivating one
 * would 404 its owner out of their own account with no self-service route back), and
 * the control is disabled here so the reason is visible rather than discovered.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PAGE_SIZE, useAdminWorkspaces, useUpdateAdminWorkspace } from '@/api/admin'
import { ApiError } from '@/api/client'
import type { AdminWorkspace } from '@/api/types'
import { Pagination, Pill, TableScroller, Td, Th } from '@/components/admin/AdminShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'

const GB = 1024 ** 3

export function AdminWorkspaces() {
  const { t } = useTranslation(['admin', 'common'])
  const { toast } = useToast()

  const [q, setQ] = useState('')
  const [offset, setOffset] = useState(0)

  const workspaces = useAdminWorkspaces({ q: q || undefined, offset })
  const update = useUpdateAdminWorkspace()

  const save = (workspaceId: number, body: { is_active?: boolean; disk_quota_bytes?: number }) =>
    update.mutate(
      { workspaceId, ...body },
      {
        onSuccess: () => toast({ title: t('common.saved', { ns: 'common' }), variant: 'success' }),
        onError: (error) =>
          toast({
            title: t('common.error', { ns: 'common' }),
            description: error instanceof ApiError ? error.localised : undefined,
            variant: 'error',
          }),
      },
    )

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('admin.workspaces.title')}</h2>

      <Input
        className="max-w-sm"
        placeholder={t('admin.workspaces.search')}
        value={q}
        onChange={(event) => {
          setQ(event.target.value)
          setOffset(0)
        }}
      />

      {workspaces.data && workspaces.data.items.length === 0 ? (
        <p className="rounded-lg border border-border p-6 text-center text-sm text-text-muted">
          {t('admin.workspaces.empty')}
        </p>
      ) : (
        <>
          <TableScroller>
            <thead>
              <tr>
                <Th>{t('common.name', { ns: 'common' })}</Th>
                <Th>{t('admin.workspaces.owner')}</Th>
                <Th>{t('admin.workspaces.members')}</Th>
                <Th>{t('admin.workspaces.projects')}</Th>
                <Th>{t('admin.workspaces.quota')}</Th>
                <Th className="text-end">{t('common.actions', { ns: 'common' })}</Th>
              </tr>
            </thead>
            <tbody>
              {workspaces.data?.items.map((row) => (
                <WorkspaceRow key={row.id} row={row} onSave={save} />
              ))}
            </tbody>
          </TableScroller>

          <Pagination
            total={workspaces.data?.total ?? 0}
            limit={PAGE_SIZE}
            offset={offset}
            onChange={setOffset}
          />
        </>
      )}
    </div>
  )
}

function WorkspaceRow({
  row,
  onSave,
}: {
  row: AdminWorkspace
  onSave: (id: number, body: { is_active?: boolean; disk_quota_bytes?: number }) => void
}) {
  const { t } = useTranslation(['admin', 'common'])
  // `null` means untouched, so the field shows the stored value without an effect to
  // keep it in sync (the same pattern as SettingsPage).
  const [draftGb, setDraftGb] = useState<string | null>(null)
  const storedGb = (row.disk_quota_bytes / GB).toFixed(0)
  const dirty = draftGb !== null && draftGb !== storedGb

  return (
    <tr>
      <Td>
        <div className="font-medium">{row.name}</div>
        <div className="text-xs text-text-muted">
          {row.slug} ·{' '}
          {row.kind === 'personal'
            ? t('admin.workspaces.kind.personal')
            : t('admin.workspaces.kind.organization')}
        </div>
        {!row.is_active && <Pill tone="fail">{t('admin.users.inactive')}</Pill>}
      </Td>
      <Td className="text-text-muted">{row.owner_username ?? '—'}</Td>
      <Td className="tabular-nums">{row.member_count}</Td>
      <Td className="tabular-nums">{row.project_count}</Td>
      <Td>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            className="h-9 w-24"
            aria-label={t('admin.workspaces.quota')}
            value={draftGb ?? storedGb}
            onChange={(event) => setDraftGb(event.target.value)}
          />
          <span className="text-xs text-text-muted">{t('admin.workspaces.quota_unit')}</span>
          {dirty && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onSave(row.id, { disk_quota_bytes: Math.max(0, Number(draftGb)) * GB })
                setDraftGb(null)
              }}
            >
              {t('action.save', { ns: 'common' })}
            </Button>
          )}
        </div>
      </Td>
      <Td>
        <div className="flex justify-end">
          <Button
            size="sm"
            variant={row.is_active ? 'danger' : 'secondary'}
            // A personal workspace is its owner's only home; deactivating it would
            // lock them out entirely. The server refuses this too.
            disabled={row.kind === 'personal'}
            onClick={() => onSave(row.id, { is_active: !row.is_active })}
          >
            {row.is_active ? t('admin.workspaces.deactivate') : t('admin.users.reactivate')}
          </Button>
        </div>
      </Td>
    </tr>
  )
}
